import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import { blogArticles, categories, type BlogCategory } from "@/data/blogArticles";

const BlogMap = () => {
  const grouped: Record<BlogCategory, typeof blogArticles> = categories.reduce(
    (acc, c) => ({ ...acc, [c]: [] }),
    {} as Record<BlogCategory, typeof blogArticles>,
  );
  blogArticles.forEach((a) => grouped[a.category].push(a));

  // Drop empty categories from display
  const nonEmpty = categories.filter((c) => grouped[c].length > 0);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.elevateglobalsolutions.net/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://www.elevateglobalsolutions.net/blog" },
      { "@type": "ListItem", position: 3, name: "Blog Map", item: "https://www.elevateglobalsolutions.net/blog/map" },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Blog Map, Articles by Category | Elevate Global</title>
        <meta
          name="description"
          content="Full map of every article on the Elevate Insights blog, grouped by category, lead generation, cold calling, outsourcing, sales strategy, digital marketing, and more."
        />
        <link rel="canonical" href="https://www.elevateglobalsolutions.net/blog/map" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <section className="section-padding">
        <div className="container-narrow max-w-5xl">
          <nav className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-primary">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-foreground">Map</span>
          </nav>

          <div className="mt-6">
            <div className="gold-divider" />
            <h1 className="mt-4 font-display text-4xl font-bold text-foreground md:text-5xl">
              Blog map
            </h1>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              Every article on the Elevate Insights blog, grouped by category. Built for visitors
              and search engines so nothing stays buried.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
              <Link
                to="/blog"
                className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 font-medium text-foreground hover:border-secondary hover:text-secondary"
              >
                <BookOpen className="mr-1.5 h-4 w-4" />
                Blog index
              </Link>
              <Link
                to="/sitemap"
                className="inline-flex items-center rounded-md border border-border bg-card px-3 py-1.5 font-medium text-foreground hover:border-secondary hover:text-secondary"
              >
                Site map
              </Link>
              <span className="text-xs text-muted-foreground">
                {blogArticles.length} articles · {nonEmpty.length} categories
              </span>
            </div>
          </div>

          {/* Category jump nav */}
          <nav aria-label="Categories" className="mt-10 flex flex-wrap gap-2">
            {nonEmpty.map((c) => (
              <a
                key={c}
                href={`#${c.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-secondary hover:text-secondary"
              >
                {c}
                <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {grouped[c].length}
                </span>
              </a>
            ))}
          </nav>

          {/* Category sections */}
          <div className="mt-12 space-y-12">
            {nonEmpty.map((c) => (
              <section
                key={c}
                id={c.toLowerCase().replace(/\s+/g, "-")}
                className="scroll-mt-24"
              >
                <div className="flex items-end justify-between border-b border-border pb-3">
                  <h2 className="font-display text-2xl font-bold text-foreground md:text-3xl">
                    {c}
                  </h2>
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    {grouped[c].length} {grouped[c].length === 1 ? "article" : "articles"}
                  </span>
                </div>

                <ul className="mt-5 grid gap-3 md:grid-cols-2">
                  {grouped[c].map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/blog/${a.slug}`}
                        className="group flex h-full flex-col rounded-xl border border-border bg-card p-5 transition-all hover:border-secondary hover:shadow-md"
                      >
                        <h3 className="font-display text-base font-bold leading-snug text-foreground group-hover:text-secondary">
                          {a.title}
                        </h3>
                        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{a.excerpt}</p>
                        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                          <span className="flex items-center gap-1.5">
                            <Clock className="h-3 w-3" />
                            {a.readingTime} min read
                          </span>
                          <span className="inline-flex items-center font-semibold text-primary group-hover:text-secondary">
                            Read
                            <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-1" />
                          </span>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 rounded-2xl border border-border bg-card p-6 text-center md:p-8">
            <h3 className="font-display text-xl font-bold text-foreground">
              Want a custom acquisition plan, not just an article?
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              30-minute strategy call, no obligation, plan emailed within 48 hours.
            </p>
            <Link
              to="/book"
              className="mt-4 inline-flex items-center rounded-md bg-secondary px-5 py-2.5 text-sm font-semibold text-secondary-foreground hover:bg-secondary/90"
            >
              Book a Free Strategy Call
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogMap;
